import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Toolbar,
  Typography,
  Box,
  TableSortLabel,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  SelectChangeEvent,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  SupervisedUserCircle as RoleIcon,
  AttachMoney as AmountIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  getUsers,
  deleteUser,
  useSecurityCheck,
  UserWithPromoCode,
  Role,
  ROLES,
  rolesStaff,
} from "../../../utils";
import { UserDialog } from "./UserDialog";
import { AmountDialog } from "./AmountDialog";
import { RoleDialog } from "./RoleDialog";
import { CustomSnackbar } from "components";
import { useAuth } from "utils/context";
import { ConfirmationDialog } from "features/reusable";

// Base columns visible to all roles
const baseHeadCells: {
  id: keyof UserWithPromoCode | "actions";
  numeric: boolean;
  label: string;
}[] = [
  { id: "username", numeric: false, label: "Nom d'utilisateur" },
  { id: "email", numeric: false, label: "Email" },
  { id: "role", numeric: false, label: "Rôle" },
  { id: "country", numeric: false, label: "Pays" },
  { id: "amount", numeric: true, label: "Montant" },
  { id: "isConfirmed", numeric: false, label: "Confirmé" },
];

export const UserTable: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === ROLES.ADMIN;

  const securityCheck = useSecurityCheck(rolesStaff);
  const [users, setUsers] = useState<UserWithPromoCode[]>([]);
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<keyof UserWithPromoCode>("username");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openRoleDialog, setOpenRoleDialog] = useState(false);
  const [openAmountDialog, setOpenAmountDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserWithPromoCode | null>(
    null
  );
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSuccess, setSnackbarSuccess] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRole, setSelectedRole] = useState<Role | "">("");
  const [total, setTotal] = useState(0);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserWithPromoCode | null>(
    null
  );
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const headCells = isAdmin
    ? [...baseHeadCells, { id: "actions", numeric: false, label: "Actions" }]
    : baseHeadCells;

  const countryOptions = Array.from(
    new Set(
      users
        .flatMap((u) =>
          Array.isArray(u.country)
            ? u.country
            : u.country
            ? [u.country as unknown as string]
            : []
        )
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b));

  const fetchUsersWithDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getUsers({
        search: searchQuery,
        page: page + 1,
        limit: rowsPerPage,
        role: selectedRole || "",
        sortBy: orderBy,
        order: order,
        country: selectedCountry || "",
      });
      setUsers(response.data?.data || []);
      setTotal(response.data?.meta?.total || 0);
    } catch (error) {
      console.error("Erreur lors du chargement des utilisateurs :", error);
    } finally {
      setLoading(false);
    }
  }, [
    searchQuery,
    page,
    rowsPerPage,
    selectedRole,
    orderBy,
    order,
    selectedCountry,
  ]);

  useEffect(() => {
    fetchUsersWithDetails();
  }, [fetchUsersWithDetails]);

  const handleRequestSort = (property: keyof UserWithPromoCode) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleCountryChange = (event: SelectChangeEvent) => {
    setSelectedCountry(event.target.value as string);
    setPage(0);
  };

  const handleRoleChange = (event: SelectChangeEvent) => {
    setSelectedRole(event.target.value as Role | "");
    setPage(0);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Opens the confirmation dialog
  const handleDeleteRequest = (user: UserWithPromoCode) => {
    setUserToDelete(user);
    setOpenConfirmDialog(true);
  };

  // Performs the delete action after confirmation
  const handleConfirmDelete = async () => {
    if (userToDelete) {
      await deleteUser(userToDelete.userID);
      fetchUsersWithDetails();
      setSnackbarMessage("Utilisateur supprimé avec succès!");
      setSnackbarSuccess(true);
      setSnackbarOpen(true);
      setOpenConfirmDialog(false);
      setUserToDelete(null);
    }
  };

  const handleEdit = (user: UserWithPromoCode) => {
    setCurrentUser(user);
    setOpenDialog(true);
  };

  const handleRole = (user: UserWithPromoCode) => {
    setCurrentUser(user);
    setOpenRoleDialog(true);
  };

  const handleAmount = (user: UserWithPromoCode) => {
    setCurrentUser(user);
    setOpenAmountDialog(true);
  };

  const handleAdd = () => {
    setCurrentUser(null);
    setOpenDialog(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (securityCheck) {
    return securityCheck;
  }

  return (
    <Box sx={{ width: "95%", marginTop: "55px" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <Toolbar>
          <Typography sx={{ flex: "1 1 100%" }} variant="h6">
            Utilisateurs
          </Typography>
          {isAdmin && (
            <Button variant="contained" color="primary" onClick={handleAdd}>
              Ajouter un utilisateur
            </Button>
          )}
        </Toolbar>

        <Box sx={{ p: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormControl sx={{ minWidth: 200, flexGrow: 1 }}>
            <InputLabel>Filtrer par rôle</InputLabel>
            <Select
              value={selectedRole}
              label="Filtrer par rôle"
              onChange={handleRoleChange}
            >
              <MenuItem value="">
                <em>Tous</em>
              </MenuItem>
              {Object.values(ROLES).map((role) => (
                <MenuItem key={role} value={role}>
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200, flexGrow: 1 }}>
            <InputLabel>Filtrer par pays</InputLabel>
            <Select
              value={selectedCountry}
              label="Filtrer par pays"
              onChange={handleCountryChange}
              displayEmpty
            >
              <MenuItem value="">
                <em>Tous</em>
              </MenuItem>
              {countryOptions.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            variant="outlined"
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 2 }}
          />
        </Box>

        <TableContainer>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="400px"
            >
              <CircularProgress />
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      sortDirection={orderBy === headCell.id ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : "asc"}
                        onClick={() =>
                          handleRequestSort(
                            headCell.id as keyof UserWithPromoCode
                          )
                        }
                      >
                        {headCell.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow hover tabIndex={-1} key={user.userID}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.role}</TableCell>
                    <TableCell>{user.country?.join(", ") || "N/A"}</TableCell>
                    <TableCell>{user.amount}</TableCell>
                    <TableCell>
                      {user.isConfirmed ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <CancelIcon color="error" />
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <Tooltip title="Modifier">
                          <IconButton onClick={() => handleEdit(user)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton onClick={() => handleDeleteRequest(user)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Gérer Rôle/Pays">
                          <IconButton onClick={() => handleRole(user)}>
                            <RoleIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Gérer le montant">
                          <IconButton onClick={() => handleAmount(user)}>
                            <AmountIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <UserDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSave={fetchUsersWithDetails}
        initialData={currentUser}
      />
      <RoleDialog
        open={openRoleDialog}
        onClose={() => setOpenRoleDialog(false)}
        onSave={fetchUsersWithDetails}
        initialData={currentUser}
      />
      <AmountDialog
        open={openAmountDialog}
        onClose={() => setOpenAmountDialog(false)}
        onSave={fetchUsersWithDetails}
        initialData={currentUser}
      />

      <ConfirmationDialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={handleConfirmDelete}
        title="Confirmer la suppression"
        description={`Êtes-vous sûr de vouloir supprimer l'utilisateur "${userToDelete?.username}"? Cette action est irréversible.`}
        confirmButtonText="Supprimer"
        confirmButtonColor="error"
      />

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        isSuccess={snackbarSuccess}
        handleClose={handleSnackbarClose}
      />
    </Box>
  );
};
